import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
} from "@mui/material";
import TelegramIcon from "@mui/icons-material/Telegram";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import UploadIcon from "@mui/icons-material/Upload";
import Toolbar from "../../../../components/core/base/Toolbar";
import DataTable from "../../../../components/core/base/DataTable";
import ColumnsModal from "../../../../components/core/base/ColumnsModal";
import FiltersModal from "../../../../components/core/base/FiltersModal";
import ContractPrintDialog from "../../../business/rental/deals/ContractPrintDialog";
import ContractEditDialog from "../../../business/rental/deals/ContractEditDialog";
import api from "../../../../components/api/api";
import rentalDealsConfig from "../../../core/config/rentalDealsConfig";
import filtersConfig from "../../../core/config/filtersConfig";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { formatLocalDateTime } from "../../../../utils/timezoneUtils";

// Ключ для сохранения состояния таблицы в localStorage
const TABLE_STATE_KEY = "table_state_rental_deals";

// Функция для загрузки сохраненного состояния таблицы
const loadTableState = () => {
  try {
    const saved = localStorage.getItem(TABLE_STATE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.warn("Ошибка загрузки состояния таблицы:", error);
  }
  return null;
};

// Функция для сохранения состояния таблицы
const saveTableState = (state) => {
  try {
    localStorage.setItem(TABLE_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn("Ошибка сохранения состояния таблицы:", error);
  }
};

const RentalDealsPage = () => {
  // Загружаем сохраненное состояние
  const savedState = loadTableState();

  const [searchParams, setSearchParams] = useSearchParams();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const requestVersion = useRef(0);
  const refreshRef = useRef(null);
  const skipLocationReload = useRef(false);
  const [columns, setColumns] = useState([]);
  const [viewMode, setViewMode] = useState("list");
  const [isColumnsModalOpen, setIsColumnsModalOpen] = useState(false);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filters, setFilters] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState(savedState?.searchQuery || "");

  // Пагинация (с восстановлением из localStorage)
  const [page, setPage] = useState(savedState?.page || 1);
  const [pageSize, setPageSize] = useState(savedState?.pageSize || 20);
  const [totalDeals, setTotalDeals] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Сортировка (с восстановлением из localStorage)
  const [sortBy, setSortBy] = useState(savedState?.sortField || "deal_number");
  const [sortOrder, setSortOrder] = useState(savedState?.sortOrder || "desc");

  // ID элемента для подсветки и скролла после возврата
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  // Состояния для печати договоров
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [contractEditDialogOpen, setContractEditDialogOpen] = useState(false);

  // Диалог отправки сообщения клиенту
  const [notifyDialog, setNotifyDialog] = useState(false);
  const [notifyDeal, setNotifyDeal] = useState(null);
  const [notifyChannel, setNotifyChannel] = useState("telegram");
  const [notifyText, setNotifyText] = useState("");
  const [notifySending, setNotifySending] = useState(false);
  const [chatOpening, setChatOpening] = useState(false);
  const chatOpeningRef = useRef(false);
  const chatRequestVersion = useRef(0);
  useEffect(() => () => { chatRequestVersion.current += 1; }, []);
  const [selectedDealForPrint, setSelectedDealForPrint] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Состояния для импорта
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  // const [businessTypeKey, setBusinessTypeKey] = useState(null); // Убрано - больше не нужно

  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const tableKey = "rental_deals";
  const filtersConfigForDeals = filtersConfig[tableKey];

  // Скролл к последнему просмотренному элементу после загрузки данных
  useEffect(() => {
    const lastViewedId = localStorage.getItem("lastViewedDealId");
    if (lastViewedId && deals.length > 0 && !loading) {
      const id = parseInt(lastViewedId, 10);
      // Проверяем, есть ли элемент в текущем списке
      const elementExists = deals.some((deal) => deal.id === id);
      if (elementExists) {
        setHighlightedRowId(id);
        // Скроллим к элементу с небольшой задержкой для рендера
        setTimeout(() => {
          const element = document.getElementById(`rental_deals-row-${id}`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 100);
        // Убираем подсветку через 2 секунды
        setTimeout(() => {
          setHighlightedRowId(null);
        }, 2000);
      }
      // Очищаем сохраненный ID
      localStorage.removeItem("lastViewedDealId");
    }
  }, [deals, loading]);

  // Загрузка сделок
  const fetchDeals = async (
    query = "",
    filters = {},
    currentPage = page,
    currentPageSize = pageSize,
    currentSortBy = sortBy,
    currentSortOrder = sortOrder,
  ) => {
    const version = ++requestVersion.current;
    try {
      setLoading(true);
      setLoadError("");

      // Формируем параметры запроса
      const params = {
        page: currentPage,
        limit: currentPageSize,
      };

      // Добавляем параметры сортировки
      if (currentSortBy) {
        params.sort_by = currentSortBy;
        params.sort_order = currentSortOrder;
      }

      // Добавляем поисковый запрос
      if (query) {
        params.search = query;
      }

      // Добавляем фильтры
      Object.keys(filters).forEach((key) => {
        const value = filters[key];

        // Обработка checkbox фильтров (boolean значения)
        if (key === "hide_closed" || key === "expiring") {
          params[key] = value === true || value === "true";
        }
        // Обработка обычных фильтров
        else if (value !== null && value !== undefined && String(value).trim() !== "") {
          params[key] = value;
        }
      });

      const response = await api.get("/rental-deals/", {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (version !== requestVersion.current) return;

      // Проверяем формат ответа - если есть пагинация
      if (
        response.data &&
        typeof response.data === "object" &&
        "items" in response.data
      ) {
        setDeals(Array.isArray(response.data.items) ? response.data.items : []);
        setTotalDeals(response.data.total || 0);
        setTotalPages(Math.ceil((response.data.total || 0) / currentPageSize));
      } else {
        // Старый формат - массив
        const items = Array.isArray(response.data) ? response.data : [];
        setDeals(items);
        setTotalDeals(items.length);
        setTotalPages(1);
      }
    } catch (error) {
      if (version !== requestVersion.current) return;
      console.error("Ошибка загрузки сделок:", error);
      setLoadError("Не удалось загрузить сделки. Попробуйте обновить список.");
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };
  refreshRef.current = () => fetchDeals(searchQuery, activeFilters);

  // Функция для принудительного обновления данных
  const refreshDeals = () => {
    loadSavedFiltersAndFetchDeals(); // Используем функцию с загрузкой сохраненных фильтров
  };

  // Обработчики пагинации
  const handlePageChange = (event, value) => {
    setPage(value);
    fetchDeals(searchQuery, activeFilters, value, pageSize, sortBy, sortOrder);
  };

  const handlePageSizeChange = (event) => {
    const newSize = event.target.value;
    setPageSize(newSize);
    setPage(1); // Сбрасываем на первую страницу
    fetchDeals(searchQuery, activeFilters, 1, newSize, sortBy, sortOrder);
  };

  // Обработчик сортировки
  const handleSort = (field) => {
    let newSortOrder = "asc";

    // Если кликнули по тому же полю, меняем порядок
    if (sortBy === field) {
      newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    }

    setSortBy(field);
    setSortOrder(newSortOrder);
    setPage(1); // Сбрасываем на первую страницу
    fetchDeals(searchQuery, activeFilters, 1, pageSize, field, newSortOrder);
  };
  // Загрузка бизнес-ключа - больше не нужна
  /*
  const fetchBusinessTypeKey = async () => {
    try {
      const response = await api.get("/settings/settings", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.data?.business_type_key) {
        setBusinessTypeKey(response.data.business_type_key);
      }
    } catch (error) {
      console.error("Ошибка загрузки ключа бизнеса:", error);
    }
  };
  */

  // Сохраняем состояние таблицы при изменении
  useEffect(() => {
    saveTableState({
      page,
      pageSize,
      sortField: sortBy,
      sortOrder,
      searchQuery,
    });
  }, [page, pageSize, sortBy, sortOrder, searchQuery]);

  // Инициализация
  useEffect(() => {
    // fetchBusinessTypeKey(); // Убрано - больше не нужно
    fetchColumns();
    initializeFilters();
  }, []);

  // Функция для загрузки сохраненных фильтров и применения их к данным
  const loadSavedFiltersAndFetchDeals = async () => {
    try {
      // Загружаем сохраненные фильтры пользователя из filter_config
      const response = await api.get("/filters/rental_deals", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.data && response.data.length > 0) {
        // Восстанавливаем значения фильтров из сохраненных данных
        const savedFilters = {};
        const activeFiltersForAPI = {};
        response.data.forEach((filter) => {
          if (
            filter.filter_type === "value" &&
            filter.options &&
            filter.options.length > 0
          ) {
            const value = filter.options[0] ?? "";

            // Обработка checkbox фильтров
            if (filter.filter_name === "hide_closed" || filter.filter_name === "expiring") {
              const boolValue =
                value === true || value === "true" || value === true;
              savedFilters[filter.filter_name] = boolValue;
              activeFiltersForAPI[filter.filter_name] = boolValue;
            }
            // Обработка обычных фильтров
            else {
              // Сохраняем все значения для отображения в FiltersModal
              savedFilters[filter.filter_name] = value;

              // Добавляем в activeFiltersForAPI только непустые значения (для активной фильтрации)
              if (String(value).trim() !== "") {
                activeFiltersForAPI[filter.filter_name] = value;
              }
            }
          }
        });

        setActiveFilters(savedFilters); // Устанавливаем все фильтры для отображения
        // Загружаем данные только с непустыми фильтрами
        fetchDeals(searchQuery, activeFiltersForAPI);
      } else {
        // Если сохраненных фильтров нет, загружаем данные без фильтров
        fetchDeals(searchQuery);
      }
    } catch (error) {
      console.error("❌ Ошибка загрузки сохраненных фильтров:", error);
      // В случае ошибки загружаем данные без фильтров
      fetchDeals(searchQuery);
    }
  };

  // Обновление данных при возврате на страницу (когда location.pathname изменяется)
  useEffect(() => {
    if (location.pathname === "/rental-deals") {
      if (skipLocationReload.current) {
        skipLocationReload.current = false;
        return;
      }
      // Проверяем URL-параметр filter=expiring (переход с дашборда)
      const filterParam = searchParams.get("filter");
      if (filterParam === "expiring") {
        // Устанавливаем фильтр "просроченные/истекающие" и сортировку по дате окончания
        const expiringFilters = { expiring: true };
        setActiveFilters(expiringFilters);
        setSortBy("end_date");
        setSortOrder("asc");
        setPage(1);
        setSearchQuery("");
        fetchDeals("", expiringFilters, 1, pageSize, "end_date", "asc");
        // Убираем параметр из URL чтобы при обновлении не зациклилось
        skipLocationReload.current = true;
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("filter");
        setSearchParams(nextParams, { replace: true });
        return;
      }
      loadSavedFiltersAndFetchDeals(); // Загружаем сохраненные фильтры вместо обычной загрузки
    }
  }, [location.pathname, location.search]);

  // Обновление данных при фокусе окна (когда пользователь возвращается в браузер)
  useEffect(() => {
    const handleFocus = () => {
      refreshRef.current();
    };

    // Добавляем слушатель события focus
    window.addEventListener("focus", handleFocus);

    // Убираем слушатель при размонтировании компонента
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, []); // Убираем зависимости, чтобы избежать лишних перерендеров

  // Слушатель для обновления данных при изменении сделок
  useEffect(() => {
    const handleDealsUpdated = () => {
      refreshRef.current();
    };

    // Добавляем слушатель кастомного события
    window.addEventListener("rentalDealsUpdated", handleDealsUpdated);

    // Убираем слушатель при размонтировании компонента
    return () => {
      window.removeEventListener("rentalDealsUpdated", handleDealsUpdated);
    };
  }, []); // Убираем зависимости

  // Проверка URL параметра deal_id для автоматического открытия сделки
  useEffect(() => {
    const dealNumber = searchParams.get("deal_id");
    if (dealNumber) {
      // Делаем API запрос для поиска сделки по deal_number
      const findAndOpenDeal = async () => {
        try {
          const response = await api.get("/rental-deals/", {
            params: { deal_number: dealNumber },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });

          const items = response.data?.items || response.data || [];
          if (items.length > 0) {
            const deal = items[0];
            // Переходим на страницу редактирования сделки
            navigate(`/rental-deals/edit/${deal.id}`);
          }
        } catch (error) {
          console.error("Ошибка поиска сделки:", error);
        }
      };

      findAndOpenDeal();
    }
  }, [searchParams, navigate]);

  // Загрузка колонок
  const fetchColumns = async () => {
    try {
      const response = await api.get("/table-columns/", {
        params: { table_key: "rental_deals" },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      // Преобразуем колонки из базы данных
      const dbColumns = response.data;

      const transformedColumns = dbColumns.map((col) => ({
        id: col.id,
        table_key: col.table_key,
        column_name: col.column_name,
        field: col.column_name,
        label: translateLabelToRussian(col.column_name), // Переводим на русский
        sortable: col.sortable,
        is_visible: col.is_visible,
        order_index: col.order_index, // Добавляем order_index
      }));

      // Сортируем колонки по order_index (NULL значения в конце)
      const sortedColumns = transformedColumns.sort((a, b) => {
        // Если у обеих колонок есть order_index
        if (a.order_index !== null && b.order_index !== null) {
          return a.order_index - b.order_index;
        }
        // Если у одной есть order_index, а у другой нет - с order_index идет первой
        if (a.order_index !== null && b.order_index === null) {
          return -1;
        }
        if (a.order_index === null && b.order_index !== null) {
          return 1;
        }
        // Если у обеих нет order_index - сортируем по ID
        return a.id - b.id;
      });

      setColumns(sortedColumns);
    } catch (error) {
      console.error("❌ Ошибка загрузки колонок:", error);
      // В случае ошибки используем статическую конфигурацию
      const fallbackColumns = rentalDealsConfig.map((config, index) => ({
        id: `fallback_${index}`, // Используем строковый ID для fallback
        table_key: "rental_deals",
        column_name: config.field,
        field: config.field,
        label: config.label,
        sortable: config.sortable,
        is_visible: true,
      }));
      setColumns(fallbackColumns);
    }
  };

  // Функция для перевода на русский язык
  const translateLabelToRussian = (columnName) => {
    const translations = {
      id: "ID",
      deal_number: "Номер сделки",
      start_date: "Дата начала",
      end_date: "Дата окончания",
      actual_close_date: "Дата закрытия",
      client_id: "ID клиента",
      "client.display_name": "Клиент",
      client_full_name: "Клиент",
      asset_id: "ID актива",
      asset_ids: "ID активов",
      "asset.name": "Актив",
      asset_names: "Активы",
      amount: "Сумма",
      amount_due: "К доплате",
      total_paid: "Оплачено",
      deposit: "Залог",
      actual_deposit: "Фактический залог",
      deposit_date: "Дата залога",
      status: "Статус",
      deal_status: "Статус сделки",
      company_id: "ID компании",
      created_at: "Дата создания",
      updated_at: "Дата обновления",
      rental_period: "Период аренды",
      notes: "Заметки",
      rental_point_id: "Пункт проката",
      tariff_id: "Тариф",
    };
    return translations[columnName] || columnName;
  };

  // Инициализация фильтров
  const initializeFilters = async () => {
    try {
      // Загружаем клиентов и активы для фильтров
      const [clientsResponse, assetsResponse] = await Promise.all([
        api.get("/clients/", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }),
        api.get("/assets/rental", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }),
      ]);

      // Извлекаем данные из пагинированных ответов
      const clientsData =
        clientsResponse.data?.items || clientsResponse.data || [];
      const assetsData =
        assetsResponse.data?.items || assetsResponse.data || [];

      const clients = clientsData.map((c) => ({
        value: c.id,
        label: c.display_name || c.name,
      }));
      const assets = assetsData.map((a) => ({
        value: a.id,
        label: a.name,
      }));

      // Обновляем конфигурацию фильтров с динамическими данными
      const dynamicFiltersConfig = filtersConfigForDeals.map((filter) => {
        if (filter.field === "client_id") {
          return { ...filter, options: clients };
        }
        if (filter.field === "asset_id") {
          return { ...filter, options: assets };
        }
        return filter;
      });

      // Преобразуем в формат для FiltersModal
      const filters = dynamicFiltersConfig.map((filter) => ({
        filter_name: filter.field,
        filter_label: filter.label,
        filter_type: filter.type,
        options: filter.options || [],
        is_visible: filter.isVisible,
        table_key: tableKey,
      }));

      setFilters(filters);
    } catch (error) {
      console.error("❌ Ошибка инициализации фильтров:", error);
    }
  };

  // Удаление выбранных сделок
  const handleDeleteSelected = async () => {
    try {
      for (let id of selectedItems) {
        await api.delete(`/rental-deals/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      }
      setSelectedItems([]);
      fetchDeals(searchQuery, activeFilters);
      enqueueSnackbar("Выбранные сделки успешно удалены", {
        variant: "success",
      });
    } catch (error) {
      console.error("Ошибка удаления выбранных сделок:", error);
      enqueueSnackbar("Ошибка при удалении сделок", { variant: "error" });
    }
  };

  // Функция для отмены сделки
  const handleCancelDeal = async (deal) => {
    try {
      const response = await api.put(
        `/rental-deals/${deal.id}`,
        { status: "отменена" },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      await fetchDeals(searchQuery, activeFilters);
      enqueueSnackbar("Сделка успешно отменена", { variant: "success" });
    } catch (error) {
      console.error("❌ Ошибка при отмене сделки:", error);
      enqueueSnackbar("Ошибка при отмене сделки", { variant: "error" });
    }
  };

  // Функция для закрытия сделки
  const handleCloseDeal = async (deal) => {
    try {
      const response = await api.post(
        `/rental-deals/${deal.id}/close`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      await fetchDeals(searchQuery, activeFilters);
      enqueueSnackbar("Сделка успешно закрыта", { variant: "success" });
    } catch (error) {
      console.error("❌ Ошибка при закрытии сделки:", error);
      enqueueSnackbar("Ошибка при закрытии сделки", { variant: "error" });
    }
  };

  // Печать договора
  const handlePrintContract = (deal) => {
    setSelectedDealForPrint(deal);
    setPrintDialogOpen(true);
  };

  const handleTemplateSelect = (template) => {
    // Сохраняем текущую сделку для использования в ContractEditDialog
    const currentDeal = selectedDealForPrint;

    setSelectedTemplate(template);
    setPrintDialogOpen(false);
    setContractEditDialogOpen(true);

    // Убеждаемся, что данные сделки остаются доступными
    if (!currentDeal) {
      console.error("❌ Нет данных сделки для передачи в ContractEditDialog");
    }
  };

  const handleClosePrintDialog = () => {
    setPrintDialogOpen(false);
    // Не сбрасываем selectedDealForPrint здесь, так как он может понадобиться для ContractEditDialog
  };

  const handleCloseContractEditDialog = () => {
    setContractEditDialogOpen(false);
    setSelectedTemplate(null);
    // Сбрасываем данные сделки только при закрытии ContractEditDialog
    setSelectedDealForPrint(null);
  };

  // Обработка поиска
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const query = searchQuery.trim();
    setSearchQuery(query);
    setPage(1);
    fetchDeals(query, activeFilters, 1, pageSize, sortBy, sortOrder);
  };

  // Изменение колонок
  const handleColumnsChange = async (updatedColumns) => {
    try {
      // Если это новые сгенерированные колонки, просто обновляем состояние
      if (updatedColumns && updatedColumns.length > 0 && updatedColumns[0].id) {
        setColumns(updatedColumns);
        enqueueSnackbar("Колонки успешно сгенерированы", {
          variant: "success",
        });
        return;
      }

      // Иначе сохраняем изменения видимости колонок
      for (const column of updatedColumns) {
        await api.put(
          `/table-columns/${column.id}`,
          {
            is_visible: column.is_visible,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
      }
      setColumns(updatedColumns);
      enqueueSnackbar("Настройки колонок сохранены", { variant: "success" });
    } catch (error) {
      console.error("Ошибка сохранения настроек колонок:", error);
      enqueueSnackbar("Ошибка сохранения настроек", { variant: "error" });
    }
  };

  // Применение фильтров
  const handleApplyFilters = (filterValues) => {
    setActiveFilters(filterValues);
    setPage(1);
    fetchDeals(searchQuery, filterValues, 1);
  };

  // Сброс фильтров
  const handleResetFilters = () => {
    setActiveFilters({});
    setPage(1);
    fetchDeals(searchQuery, {}, 1);
  };

  // Форматирование суммы
  const formatAmount = (amount) => {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: "RUB",
    }).format(amount);
  };

  // Форматирование даты с временем с учетом локального часового пояса
  const formatDateTime = (dateString) => {
    return formatLocalDateTime(dateString, true);
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    return formatLocalDateTime(dateString, false);
  };

  const handleOpenClientChat = async (deal) => {
    if (chatOpeningRef.current) return;
    chatOpeningRef.current = true;
    const version = ++chatRequestVersion.current;
    setChatOpening(true);
    try {
      const { data } = await api.post(`/chats/for-deal/${deal.id}`);
      if (version !== chatRequestVersion.current) return;
      if (!Number.isSafeInteger(data?.id) || data.id < 1) throw new Error('Invalid chat');
      navigate(`/messenger?chat=${data.id}`);
    } catch (error) {
      if (version !== chatRequestVersion.current) return;
      const detail = error.response?.data?.detail;
      enqueueSnackbar(typeof detail === 'string' ? detail : 'Не удалось открыть чат клиента. Попробуйте ещё раз.', { variant: 'error' });
    } finally {
      if (version === chatRequestVersion.current) {
        chatOpeningRef.current = false;
        setChatOpening(false);
      }
    }
  };

  // Отдельное явное действие для внешнего Telegram, не чат кабинета.
  const handleOpenNotify = (deal) => {
    setNotifyDeal(deal);
    setNotifyText("");
    setNotifyChannel("personal_telegram");
    setNotifyDialog(true);
  };

  const handleSendNotify = async () => {
    if (!notifyText.trim()) {
      enqueueSnackbar("Введите текст сообщения", { variant: "warning" });
      return;
    }
    setNotifySending(true);
    try {
      await api.post(
        `/rental-deals/${notifyDeal.id}/notify`,
        { channel: notifyChannel, text: notifyText },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );
      enqueueSnackbar("Сообщение отправлено!", { variant: "success" });
      setNotifyDialog(false);
    } catch (err) {
      const msg = err.response?.data?.detail || "Ошибка отправки";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setNotifySending(false);
    }
  };

  // Открытие формы для создания новой сделки
  const handleCreateDeal = () => {
    navigate("/rental-deals/edit/new");
  };

  // Обработчик импорта сделок
  const handleImport = async () => {
    if (!importFile) {
      enqueueSnackbar("Выберите файл для импорта", { variant: "warning" });
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append("file", importFile);

    try {
      const response = await api.post("/rental-deals/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      enqueueSnackbar(
        `Импортировано: ${response.data.imported}, Пропущено: ${response.data.skipped}`,
        { variant: "success" },
      );

      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.forEach((error) => {
          enqueueSnackbar(error, { variant: "warning" });
        });
      }

      setImportDialog(false);
      setImportFile(null);
      fetchDeals(searchQuery, activeFilters);
    } catch (error) {
      console.error("Ошибка импорта:", error);
      enqueueSnackbar(
        error.response?.data?.detail || "Ошибка при импорте сделок",
        { variant: "error" },
      );
    } finally {
      setImporting(false);
    }
  };

  // Скачивание шаблона для импорта
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get("/rental-deals/import/template", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "template_deals.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      enqueueSnackbar("Шаблон скачан", { variant: "success" });
    } catch (error) {
      console.error("Ошибка скачивания шаблона:", error);
      enqueueSnackbar("Ошибка при скачивании шаблона", { variant: "error" });
    }
  };

  // Открытие формы для редактирования сделки
  const handleEditDeal = (deal) => {
    navigate(`/rental-deals/edit/${deal.id}`);
  };

  // Получение статуса сделки
  const getDealStatus = (deal) => {
    if (deal.return_info) {
      const labels = { requested: 'Клиент хочет сдать вещь', received: 'Вещь на проверке', refund_pending: 'Возврат денег обрабатывается' };
      if (labels[deal.return_info.status]) return { label: labels[deal.return_info.status], color: 'warning' };
    }
    // Если есть поле status в сделке - используем его
    if (deal.status) {
      const statusMapping = {
        в_процессе: { label: "В процессе", color: "info" },
        просрочена: { label: "Просрочена", color: "error" },
        закрыта: { label: "Закрыта", color: "success" },
        отменена: { label: "Отменена", color: "default" },
      };
      return (
        statusMapping[deal.status] || { label: deal.status, color: "default" }
      );
    }

    // Если нет статуса - определяем по датам
    const now = new Date();
    const start = new Date(deal.start_date);
    const end = deal.end_date ? new Date(deal.end_date) : null;

    if (now < start) {
      return { label: "Запланирована", color: "info" };
    } else if (!end || now <= end) {
      return { label: "Активна", color: "success" };
    } else {
      return { label: "Завершена", color: "default" };
    }
  };

  // Получение отображаемого статуса сделки
  const getDealStatusDisplay = (status) => {
    const statusMapping = {
      в_процессе: "В процессе",
      просрочена: "Просрочена",
      закрыта: "Закрыта",
      отменена: "Отменена",
    };
    return statusMapping[status] || status || "В процессе";
  };

  // Получение полного имени клиента
  const getClientFullName = (client) => {
    if (!client) return "Клиент не найден";

    if (client.display_name) return client.display_name;
    if (client.name) return client.name;

    const parts = [];
    if (client.first_name) parts.push(client.first_name);
    if (client.last_name) parts.push(client.last_name);
    if (client.middle_name) parts.push(client.middle_name);

    return parts.length > 0 ? parts.join(" ") : `Клиент #${client.id || "N/A"}`;
  };

  // Получение отображения периода аренды
  const getRentalPeriodDisplay = (
    startDate,
    endDate,
    actualCloseDate,
    status,
  ) => {
    if (!startDate) return "Даты не указаны";

    const start = formatDate(startDate);
    const end = endDate ? formatDate(endDate) : "Не указан";

    // Если сделка закрыта и есть фактическая дата закрытия, показываем обе даты
    if (
      (status === "закрыта" || status === "отменена") &&
      actualCloseDate &&
      actualCloseDate !== endDate
    ) {
      const actualClose = formatDate(actualCloseDate);
      return `${start} - ${end} (закрыта ${actualClose})`;
    }

    return `${start} - ${end}`;
  };

  // Получение названий активов
  const getAssetNames = (deal) => {
    // ПРИОРИТЕТ 1: Если бэкенд уже подготовил asset_names
    if (deal.asset_names) {
      return deal.asset_names;
    }

    // ПРИОРИТЕТ 2: Если есть связанные rental_assets с информацией об активах
    if (
      deal.rental_assets &&
      Array.isArray(deal.rental_assets) &&
      deal.rental_assets.length > 0
    ) {
      const assetNames = deal.rental_assets
        .map((rentalAsset) => rentalAsset.asset?.name || "Неизвестный актив")
        .filter((name) => name !== "Неизвестный актив");

      return assetNames.length > 0 ? assetNames.join(", ") : "Нет активов";
    }

    // ПРИОРИТЕТ 3: Если активы загружены как массив
    if (deal.assets && Array.isArray(deal.assets) && deal.assets.length > 0) {
      const names = deal.assets.map((asset) => asset.name).join(", ");
      return names;
    }

    // ПРИОРИТЕТ 4: Если есть asset_ids - показываем количество с попыткой загрузить названия
    if (
      deal.asset_ids &&
      Array.isArray(deal.asset_ids) &&
      deal.asset_ids.length > 0
    ) {
      return `Активы (${deal.asset_ids.length})`;
    }

    // ПРИОРИТЕТ 5: Если есть просто asset (старый формат) - ТОЛЬКО если нет asset_ids
    if (deal.asset?.name && (!deal.asset_ids || deal.asset_ids.length === 0)) {
      return deal.asset.name;
    }

    // ПРИОРИТЕТ 6: ТОЛЬКО если нет asset_ids и asset, используем старое поле asset_id
    if (
      deal.asset_id &&
      (!deal.asset_ids || deal.asset_ids.length === 0) &&
      !deal.asset?.name
    ) {
      return `Актив #${deal.asset_id}`;
    }

    return "Не указан";
  };

  // Функция для расчета итоговой суммы с учетом скидки/наценки
  const calculateTotalAmountWithDiscount = (deal) => {
    let totalAmount = parseFloat(deal.amount || 0);
    const discountAmount = parseFloat(deal.discount_amount || 0);
    const discountType = deal.discount_type || "amount";

    // Применяем скидку/наценку
    if (discountType === "percent") {
      // Процентная скидка/наценка
      totalAmount = totalAmount * (1 + discountAmount / 100);
    } else {
      // Фиксированная скидка/наценка (положительное значение = наценка, отрицательное = скидка)
      totalAmount = totalAmount + discountAmount;
    }

    return totalAmount;
  };

  // Функция для расчета суммы к доплате с учетом скидки
  const calculateAmountDueWithDiscount = (deal) => {
    let totalAmount = parseFloat(deal.amount || 0);
    const discountAmount = parseFloat(deal.discount_amount || 0);
    const discountType = deal.discount_type || "amount";
    const totalPaid = parseFloat(deal.payment_summary?.total_paid || 0);

    // Применяем скидку/наценку
    if (discountType === "percent") {
      // Процентная скидка/наценка
      totalAmount = totalAmount * (1 + discountAmount / 100);
    } else {
      // Фиксированная скидка/наценка (положительное значение = наценка, отрицательное = скидка)
      totalAmount = totalAmount + discountAmount;
    }

    // Возвращаем сумму к доплате (итоговая сумма минус уплаченное)
    const amountDue = Math.max(0, totalAmount - totalPaid);

    return amountDue;
  };

  // Обработка данных для таблицы
  const prepareDataForTable = (deals) => {
    if (!deals || !Array.isArray(deals)) {
      return [];
    }

    return deals.map((deal) => {
      // Получаем статус сделки
      const status = getDealStatus(deal);

      const calculatedAmount = calculateTotalAmountWithDiscount(deal);

      // Форматируем данные с виртуальными полями
      const result = {
        ...deal,
        amount: calculatedAmount, // 🔧 ЗАМЕНЯЕМ amount на рассчитанное значение
        formattedStartDate: deal.start_date
          ? formatDateTime(deal.start_date)
          : "",
        formattedEndDate: deal.end_date
          ? formatDateTime(deal.end_date)
          : "Не указана",
        total_amount_with_discount: calculatedAmount, // 🔧 ИСПРАВЛЕНО: прямо подставляем рассчитанное значение
        formattedAmountDue: formatAmount(calculateAmountDueWithDiscount(deal)),
        formattedTotalPaid: formatAmount(deal.payment_summary?.total_paid || 0),
        // Сохраняем только данные статуса, без JSX
        statusData: status,

        // Виртуальные поля для лучшего отображения
        client_full_name: getClientFullName(deal.client),
        asset_names: getAssetNames(deal),
        rental_period: getRentalPeriodDisplay(
          deal.start_date,
          deal.end_date,
          deal.actual_close_date,
          deal.status,
        ),
        deal_status: deal.return_info ? status.label : getDealStatusDisplay(deal.status),
      };

      return result;
    });
  };

  // Принудительная перезагрузка колонок
  const reloadColumns = async () => {
    await fetchColumns();
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Тулбар с поиском, фильтрами и настройками колонок */}{" "}
      <Toolbar
        currentPage="rental_deals"
        // businessTypeKey={businessTypeKey} // Убрано - больше не нужно
        onAddClick={handleCreateDeal}
        onViewChange={setViewMode}
        onRefresh={() =>
          fetchDeals(
            searchQuery,
            activeFilters,
            page,
            pageSize,
            sortBy,
            sortOrder,
          )
        }
        onExport={() => {}}
        onOpenColumnsModal={() => setIsColumnsModalOpen(true)}
        onOpenFiltersModal={() => setIsFiltersModalOpen(true)}
        totalRecords={totalDeals}
        recordsLabel="аренд"
        pageSize={pageSize}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(1); // Сбрасываем на первую страницу
          fetchDeals(searchQuery, activeFilters, 1, newSize, sortBy, sortOrder);
        }}
        onDeleteSelected={handleDeleteSelected}
        selectedItems={selectedItems}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearchSubmit}
        onSearchClear={() => {
          setSearchQuery("");
          setPage(1);
          fetchDeals("", activeFilters, 1);
        }}
        customButtons={[
          {
            label: "Импорт",
            icon: <UploadIcon />,
            onClick: () => setImportDialog(true),
          },
        ]}
      />
      {/* Таблица сделок */}
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      <DataTable
        data={prepareDataForTable(deals) || []}
        config={(columns || []).filter((col) => col.is_visible)}
        viewMode={viewMode}
        sortField={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        onToolbarData={({ selectedItems }) => setSelectedItems(selectedItems)}
        selectedItems={selectedItems}
        highlightedRowId={highlightedRowId}
        tableKey="rental_deals"
        actions={[
          {
            label: "Редактировать",
            onClick: (id) => {
              // Сохраняем ID сделки для скролла при возврате
              localStorage.setItem("lastViewedDealId", id.toString());
              const deal = deals.find((d) => d.id === id);
              if (deal) handleEditDeal(deal);
            },
            tooltip: "Редактировать сделку",
          },
          {
            label: "Отменить",
            onClick: (id) => {
              const deal = deals.find((d) => d.id === id);
              if (deal) handleCancelDeal(deal);
            },
            color: "warning",
            tooltip: "Отменить сделку",
          },
          {
            label: "Закрыть",
            onClick: (id) => {
              const deal = deals.find((d) => d.id === id);
              if (deal) handleCloseDeal(deal);
            },
            color: "success",
            tooltip: "Закрыть сделку",
          },
          {
            label: "Распечатать",
            onClick: (id) => {
              const deal = deals.find((d) => d.id === id);
              if (deal) handlePrintContract(deal);
            },
            color: "primary",
            tooltip: "Распечатать договор",
          },
          {
            label: chatOpening ? "Открываем чат…" : "Написать",
            onClick: (id) => {
              const deal = deals.find((d) => d.id === id);
              if (deal) handleOpenClientChat(deal);
            },
            color: "info",
            tooltip: "Открыть чат личного кабинета клиента",
          },
          {
            label: "Telegram",
            onClick: (id) => {
              const deal = deals.find((d) => d.id === id);
              if (deal) handleOpenNotify(deal);
            },
            color: "info",
            tooltip: "Отправить сообщение через Telegram",
          },
        ]}
      />
      {/* Панель пагинации */}
      <Box
        sx={{
          mt: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Показано {deals.length} из {totalDeals} сделок
        </Typography>

        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        )}
      </Box>
      {/* Модальное окно для настройки колонок */}
      <ColumnsModal
        open={isColumnsModalOpen}
        onClose={() => setIsColumnsModalOpen(false)}
        config={columns || []}
        onColumnsChange={handleColumnsChange}
        tableKey="rental_deals"
        onReload={reloadColumns}
      />
      {/* Модальное окно для фильтров */}
      <FiltersModal
        open={isFiltersModalOpen}
        onClose={() => setIsFiltersModalOpen(false)}
        filters={filters || []}
        activeFilters={activeFilters}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
        tableKey={tableKey}
      />
      {/* Диалог выбора шаблона для печати */}
      <ContractPrintDialog
        open={printDialogOpen}
        onClose={handleClosePrintDialog}
        deal={selectedDealForPrint}
        onPrint={handleTemplateSelect}
      />
      {/* Диалог редактирования договора */}
      <ContractEditDialog
        open={contractEditDialogOpen}
        onClose={handleCloseContractEditDialog}
        deal={selectedDealForPrint}
        template={selectedTemplate}
      />
      {/* Диалог импорта сделок */}
      <Dialog
        open={importDialog}
        onClose={() => !importing && setImportDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Импорт сделок</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              1. Скачайте шаблон Excel
            </Typography>
            <Button
              variant="outlined"
              onClick={handleDownloadTemplate}
              sx={{ mb: 2 }}
              fullWidth
            >
              Скачать шаблон
            </Button>

            <Typography variant="body2" gutterBottom>
              2. Заполните данные в шаблоне
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: "block" }}
            >
              <strong>Обязательные поля:</strong>
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: "block", ml: 2 }}
            >
              • Номер сделки
              <br />
              • Клиент (ФИО, ID или телефон)
              <br />
              • Дата начала
              <br />
              • Активы (инв. номера)
              <br />• Сумма
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 2, display: "block" }}
            >
              <strong>Дополнительные поля:</strong>
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 2, display: "block", ml: 2 }}
            >
              • Дата окончания
              <br />
              • Залог
              <br />• <strong>Оплачено</strong> (будет создан платеж!)
              <br />
              • Статус (в_процессе/закрыта/просрочена/отменена)
              <br />• Дата закрытия (для закрытых сделок)
            </Typography>

            <Typography variant="body2" gutterBottom>
              3. Загрузите заполненный файл
            </Typography>
            <Button variant="outlined" component="label" fullWidth>
              {importFile ? importFile.name : "Выбрать файл"}
              <input
                type="file"
                hidden
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setImportFile(e.target.files[0])}
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialog(false)} disabled={importing}>
            Отмена
          </Button>
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={!importFile || importing}
          >
            {importing ? "Импортирование..." : "Импортировать"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Диалог: отправить сообщение клиенту */}
      <Dialog
        open={notifyDialog}
        onClose={() => setNotifyDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Сообщение в Telegram
          {notifyDeal && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Сделка #{notifyDeal.deal_number} —{" "}
              {notifyDeal.client_full_name ||
                notifyDeal.client?.name ||
                "Клиент"}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Канал отправки
            </Typography>
            <ToggleButtonGroup
              value={notifyChannel}
              exclusive
              onChange={(_, val) => {
                if (val) setNotifyChannel(val);
              }}
              size="small"
            >
              <ToggleButton value="personal_telegram" sx={{ gap: 0.5 }}>
                <TelegramIcon fontSize="small" />
                Личный аккаунт
              </ToggleButton>
              <ToggleButton value="telegram" sx={{ gap: 0.5 }}>
                <TelegramIcon fontSize="small" />
                Бот
              </ToggleButton>
              <ToggleButton value="avito" sx={{ gap: 0.5 }} disabled>
                <ChatBubbleOutlineIcon fontSize="small" />
                Avito (скоро)
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <TextField
            label="Текст сообщения"
            multiline
            rows={4}
            fullWidth
            value={notifyText}
            onChange={(e) => setNotifyText(e.target.value)}
            placeholder="Введите текст, который получит клиент..."
            helperText={`${notifyText.length} символов`}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setNotifyDialog(false)}
            disabled={notifySending}
          >
            Отмена
          </Button>
          <Button
            onClick={handleSendNotify}
            variant="contained"
            color="primary"
            disabled={!notifyText.trim() || notifySending}
            startIcon={
              notifySending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <TelegramIcon />
              )
            }
          >
            {notifySending ? "Отправка..." : "Отправить"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RentalDealsPage;
